import { ControlPlaneClient } from '@vendia/control-plane-api'
import debug from 'debug'
import { useContext, useMemo } from 'react'
import { useRecoilValue } from 'recoil'
import config from 'src/config'

import { authContext, AuthStatus, isTokenExpired } from '../auth/auth-context'
import { refreshSession } from '../auth/cognito'
import { selectedRoleState } from '../state'
import { useCurrentRoute } from './use-current-route'

const logger = debug('app:useControlPlaneApi')

const useControlPlaneApi = () => {
  const route = useCurrentRoute()
  const { setAuthStatus, auth } = useContext(authContext)
  const selectedRole = useRecoilValue(selectedRoleState)

  return useMemo(
    () =>
      new ControlPlaneClient({
        endpoint: config.shareApi,
        getCredentials: async () => {
          if (isTokenExpired(auth.idToken)) {
            logger('Token expired, attempting to refresh')
            const newSession = await refreshSession()
            setAuthStatus(newSession as AuthStatus)
            return newSession?.idToken
          }

          return auth.idToken
        },
        getAsRole: () => {
          return selectedRole?.name ?? undefined
        },
        onError: async (error: unknown) => {
          // API 401/403 error or GraphQL UNAUTHORIZED error
          const err = error as { status?: number; errors?: { extensions?: { code?: string } }[] }
          const isAuthError =
            err.status === 401 || err.status === 403 || err.errors?.some((e) => e?.extensions?.code === 'UNAUTHORIZED')
          if (isAuthError) {
            logger('API auth error, attempting to refresh token')
            try {
              const newSession = await refreshSession()
              setAuthStatus(newSession as AuthStatus)
            } catch (e) {
              logger('Failed to refresh session', e)
              window.location.href = `${window.location.origin}/login`
            }
          }
        },
        headers: {
          'x-vendia-client': 'app',
          'x-vendia-feature': () => route ?? undefined,
          'x-vendia-role-name': () => {
            return selectedRole?.name ?? undefined
          },
        },
      }),
    [auth.idToken, selectedRole?.name, route],
  )
}

export default useControlPlaneApi
